'use client'

import { useMemo } from 'react'
import classNames from 'classnames/bind'

import { Theme } from '../types'

import Card from '../Card'
import Cell from '../Cell'

import styles from './Loader.scss'

enum Size {
  Small = 'small',
  Medium = 'medium',
  Default = 'default',
  Large = 'large',
  XLarge = 'x-large',
  X2Large = 'x2-large',
  X3Large = 'x3-large',
}

enum Styling {
  Lifted = 'lifted',
}

enum State {
  Success = 'success',
  Fail = 'fail',
}

type Props = {
  size?: Size | `${Size}`
  state?: State | `${State}`
  styling?: Styling | `${Styling}` | null
  theme?: Theme<
    'primary' | 'amplified' | 'muted' | 'success' | 'warning' | 'expose' | 'transparent'
  > | null
  testId?: string
}

// eslint-disable-next-line id-length
const circleProps = (offset: number, radius: number) => ({ cx: offset, cy: offset, r: radius })

const LOADER_CIRCLE_SIZE_MAP = {
  [Size.Small]: circleProps(5, 4),
  [Size.Medium]: circleProps(7, 6),
  [Size.Default]: circleProps(10, 9),
  [Size.Large]: circleProps(14, 12.5),
  [Size.XLarge]: circleProps(20, 18),
  [Size.X2Large]: circleProps(30, 27),
  [Size.X3Large]: circleProps(40, 36),
}

const cssClasses = classNames.bind(styles)

const Loader = ({ size = Size.Default, state, styling, theme = 'primary', testId }: Props) => {
  const renderedLoader = useMemo(() => {
    const loaderSize = LOADER_CIRCLE_SIZE_MAP[size]
    const loaderClass = cssClasses(styles.loader, size, state, styling, theme)

    return (
      <div className={loaderClass} data-testid={testId} role="progressbar">
        <svg className={cssClasses('loader-svg')}>
          <circle className={cssClasses('circle')} {...loaderSize} />
        </svg>
      </div>
    )
  }, [size, state, styling, theme, testId])

  if (styling !== Styling.Lifted) return renderedLoader

  return (
    <div className={cssClasses('container')}>
      <Card styling={Card.Styling.Elevated}>
        <div className={cssClasses('wrapper')}>
          <Cell>{renderedLoader}</Cell>
        </div>
      </Card>
    </div>
  )
}

Loader.Size = Size
Loader.Styling = Styling
Loader.State = State

export default Loader
